import type { DraftStep } from "@zworks/console-contracts";
import * as React from "react";
import {
  createBezierCurve,
  createRejectRouteCurve,
  PORT_RADIUS,
  type NodeLayout,
} from "@/lib/flow-layout";

export function CanvasEdges({
  steps,
  layouts,
}: {
  steps: DraftStep[];
  layouts: Map<string, NodeLayout>;
}) {
  // Check if any step explicitly defines dependencies
  const hasExplicitDependencies = steps.some((s) => s.dependsOn && s.dependsOn.length > 0);

  // Compute all directed edges (sourceId -> targetId)
  const edges: { id: string; sourceId: string; targetId: string }[] = [];

  if (hasExplicitDependencies) {
    steps.forEach((step) => {
      if (step.dependsOn && step.dependsOn.length > 0) {
        step.dependsOn.forEach((parentId) => {
          if (layouts.has(parentId)) {
            edges.push({
              id: `edge-${parentId}-${step.id}`,
              sourceId: parentId,
              targetId: step.id,
            });
          }
        });
      }
    });
  } else {
    // Sequential fallback: step[i] -> step[i+1]
    steps.forEach((step, index) => {
      if (index === steps.length - 1) return;
      const nextStep = steps[index + 1];
      if (nextStep && layouts.has(nextStep.id)) {
        edges.push({
          id: `edge-${step.id}-${nextStep.id}`,
          sourceId: step.id,
          targetId: nextStep.id,
        });
      }
    });
  }

  return (
    <svg
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 size-full overflow-visible z-20"
    >
      <defs>
        <marker
          id="edge-arrow-default"
          viewBox="0 0 10 10"
          refX="8"
          refY="5"
          markerWidth="7"
          markerHeight="7"
          orient="auto"
        >
          <path d="M 0 1.5 L 8 5 L 0 8.5 z" className="fill-primary" />
        </marker>

        <marker
          id="edge-arrow-reject"
          viewBox="0 0 10 10"
          refX="8"
          refY="5"
          markerWidth="7"
          markerHeight="7"
          orient="auto"
        >
          <path d="M 0 1.5 L 8 5 L 0 8.5 z" className="fill-danger" />
        </marker>

        <filter id="edge-glow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="0" stdDeviation="3" floodOpacity="0.25" />
        </filter>
      </defs>

      {/* DAG / Sequential Forward Edges (Supports parallel branching & fan-in) */}
      {edges.map(({ id, sourceId, targetId }) => {
        const sourceStep = steps.find((s) => s.id === sourceId);
        const currentLayout = layouts.get(sourceId);
        const nextLayout = layouts.get(targetId);
        if (!currentLayout || !nextLayout || !sourceStep) return null;

        const start =
          sourceStep.gate && currentLayout.approvePort
            ? currentLayout.approvePort
            : currentLayout.outputPort;
        const end = nextLayout.inputPort;

        const targetX = end.x;
        const targetY = end.y - PORT_RADIUS;

        const pathData = createBezierCurve(start.x, start.y, targetX, targetY);

        return (
          <g key={id} className="transition-all duration-300">
            {/* Edge background glow */}
            <path
              d={pathData}
              fill="none"
              stroke="currentColor"
              strokeWidth="5"
              className="text-accent-text/15"
            />
            {/* Main connecting line */}
            <path
              d={pathData}
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              markerEnd="url(#edge-arrow-default)"
              className="text-accent-text"
            />
          </g>
        );
      })}

      {/* Gate Reject Routes */}
      {steps.map((step) => {
        if (!step.gate || !step.rejectRoute) return null;

        const currentLayout = layouts.get(step.id);
        const targetLayout = layouts.get(step.rejectRoute);
        if (!currentLayout || !targetLayout || !currentLayout.rejectPort) return null;

        const start = currentLayout.rejectPort;
        const end = targetLayout.inputPort;

        const targetX = end.x;
        const targetY = end.y - PORT_RADIUS;

        const pathData = createRejectRouteCurve(start.x, start.y, targetX, targetY);
        const midX = (start.x + targetX) / 2;
        const midY = Math.max(start.y, targetY) + 85;

        return (
          <g
            key={`reject-edge-${step.id}-${step.rejectRoute}`}
            className="transition-all duration-300"
          >
            {/* Reject loop background */}
            <path
              d={pathData}
              fill="none"
              stroke="currentColor"
              strokeWidth="5"
              className="text-danger/15"
            />
            {/* Reject loop main dashed line */}
            <path
              d={pathData}
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeDasharray="6 4"
              strokeLinecap="round"
              markerEnd="url(#edge-arrow-reject)"
              className="text-danger"
            />
            {/* Reject Route Indicator Tag */}
            <g transform={`translate(${midX}, ${midY})`}>
              <rect
                x="-36"
                y="-10"
                width="72"
                height="20"
                rx="10"
                className="fill-background stroke-danger/60 stroke-1 shadow-sm"
              />
              <text
                x="0"
                y="3.5"
                textAnchor="middle"
                className="fill-danger text-xs font-semibold tracking-wide uppercase select-none"
              >
                Reject
              </text>
            </g>
          </g>
        );
      })}
    </svg>
  );
}
